async function getCartToken() {
	const response = await fetch('/cart.js');
	const cart = await response.json();
	const cartId = cart.token;
	return cartId;
}

// Cross-domain tracking cookie handler
const CrossDomainTracking = {
	// Cookie domain - should be set to your root domain (e.g., .yourdomain.com)
	cookieDomain: '.feltright.com',

	// Cookie name for storing tracking data
	cookieName: 'cross_domain_tracking',

	// Debounce timeout in milliseconds
	debounceTimeout: 1000,

	// Current tracking data
	currentData: null,

	// Initialize tracking
	async init() {
		await this.setupTrackingData();
		this.setupEventListeners();
	},

	// Setup tracking data from various sources
	async setupTrackingData() {
		const newData = {
			clarity: this.getClarityData(),
			ga: this.getGAData(),
			fb: this.getFBData(),
			shopify: await this.getShopifyData(),
			timestamp: new Date().getTime()
		};

		// Only update cookie if data has changed
		if (JSON.stringify(newData) !== JSON.stringify(this.currentData)) {
			this.currentData = newData;
			this.setCookie(newData);
		}
	},

	// Debounced update function
	debouncedUpdate: null,

	// Handle updates with debouncing
	handleUpdate() {
		if (this.debouncedUpdate) {
			clearTimeout(this.debouncedUpdate);
		}
		this.debouncedUpdate = setTimeout(() => {
			this.setupTrackingData();
		}, this.debounceTimeout);
	},

	// Get Microsoft Clarity data
	getClarityData() {
		return {
			sessionId: window.clarity?.getSessionId?.() || null,
			userId: window.clarity?.getUserId?.() || null
		};
	},

	// Get Google Analytics data
	getGAData() {
		return {
			clientId: this.getGAClientId(),
			sessionId: this.getGASessionId()
		};
	},

	// Get Facebook Pixel data
	getFBData() {
		return {
			fbp: this.getCookie('_fbp'),
			fbc: this.getCookie('_fbc')
		};
	},

	// Get Shopify data
	async getShopifyData() {
		try {
			const cartToken = await getCartToken();
			return {
				cartToken: cartToken || null
			};
		} catch (error) {
			return {
				cartToken: null
			};
		}
	},

	// Get GA Client ID from cookie
	getGAClientId() {
		const gaCookie = this.getCookie('_ga');
		if (gaCookie) {
			return gaCookie.split('.')[2] + '.' + gaCookie.split('.')[3];
		}
		return null;
	},

	// Get GA Session ID from cookie
	getGASessionId() {
		const gaCookie = this.getCookie('_ga');
		if (gaCookie) {
			return gaCookie.split('.')[4];
		}
		return null;
	},

	// Set cookie with tracking data
	setCookie(data) {
		try {
			const cookieValue = JSON.stringify(data);
			// Encode the data in base64
			const encodedValue = btoa(cookieValue);
			document.cookie = `${this.cookieName}=${encodeURIComponent(encodedValue)};domain=${this.cookieDomain};path=/;max-age=31536000;SameSite=Strict`;
		} catch (error) {
			// Silently fail if cookie setting fails
		}
	},

	// Get cookie value
	getCookie(name) {
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);
		if (parts.length === 2) {
			const encodedValue = parts.pop().split(';').shift();
			try {
				// Decode the base64 value
				return atob(decodeURIComponent(encodedValue));
			} catch (error) {
				return null;
			}
		}
		return null;
	},

	// Setup event listeners for tracking updates
	setupEventListeners() {
		// Listen for Shopify cart updates
		document.addEventListener('cart:updated', () => {
			this.handleUpdate();
		});

		// Listen for customer login
		document.addEventListener('customer:logged_in', () => {
			this.handleUpdate();
		});

		// Listen for clarity updates
		if (window.clarity) {
			window.clarity.addEventListener('session:started', () => {
				this.handleUpdate();
			});
		}

		// Listen for GA updates
		window.addEventListener('gtag:event', () => {
			this.handleUpdate();
		});
	}
};

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', async () => {
	await CrossDomainTracking.init();
}); 